import { getApiGatewayUrl } from "@campgladiator/cg-common.config.config";
import {
  Request,
  NoteDTO,
  NoteRequestDTO,
  BaseEnumerableResponse,
  UserDTO,
  UserUpdateRequestDTO,
} from "@campgladiator/cg-common.types.types";
import { fetchRequest } from "@campgladiator/cg-common.api.fetch-request";

export const fetchNotesByUserId = async (
  userId: string,
  token: string,
  page?: number,
  size?: number,
  sort?: "ASC" | "DESC"
): Promise<BaseEnumerableResponse<NoteDTO>> => {
  const params = new URLSearchParams({
    ...(page && { page: page.toString() }),
    ...(size && { size: size.toString() }),
    ...(sort && { sort }),
  });
  const endpoint = `/user/v1/users/${userId}/notes?${params.toString()}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
        authToken: token,
      },
    };
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const fetchNoteByNoteId = async (
  noteId: string,
  token: string
): Promise<NoteDTO> => {
  const endpoint = `/user/v1/users/notes/${noteId}`;
  const method = "GET";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
        authToken: token,
      },
    };
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const createNoteByUserId = async (
  userId: string,
  payload: NoteRequestDTO,
  token: string
): Promise<NoteDTO> => {
  const endpoint = `/user/v1/users/${userId}/notes`;

  const request: Request = {
    requestConfig: {
      method: "POST",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint,
      },
      authToken: token,
    },
    body: JSON.stringify(payload),
  };

  try {
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const updateNoteByNoteId = async (
  noteId: string,
  payload: NoteRequestDTO,
  token: string
): Promise<NoteDTO> => {
  const endpoint = `/user/v1/users/notes/${noteId}`;

  const request: Request = {
    requestConfig: {
      method: "PUT",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint,
      },
      authToken: token,
    },
    body: JSON.stringify(payload),
  };

  try {
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const deleteNoteByNoteId = async (
  noteId: string,
  token: string
): Promise<boolean> => {
  const endpoint = `/user/v1/users/notes/${noteId}`;
  const method = "DELETE";

  try {
    const request: Request = {
      requestConfig: {
        method,
        url: { baseUrl: getApiGatewayUrl(), endpoint },
        authToken: token,
      },
    };
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};

export const createUser = async (
  token: string,
  user: Partial<UserUpdateRequestDTO>
): Promise<UserDTO> => {
  const endpoint = `/user/v1/users`;

  const request: Request = {
    requestConfig: {
      method: "POST",
      url: {
        baseUrl: getApiGatewayUrl(),
        endpoint,
      },
      authToken: token,
    },
    body: JSON.stringify(user),
  };

  try {
    const response = await fetchRequest({ request });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = (await response.json()) as UserDTO;

    return result;
  } catch (error) {
    let errorMessage = error instanceof Error ? error.message : error;
    errorMessage = errorMessage || "An unknown error occurred";

    throw new Error(errorMessage as string);
  }
};
